import { PartnershipConfig } from '@/types/partnership.types';

/**
 * Partnership Template
 * 
 * Copy this file and rename it to your partnership ID (e.g., gates-foundation.ts)
 * Fill in all the required fields below.
 * 
 * REQUIRED ASSETS:
 * - Partner logo image in /public/uploads/
 * - Brand colors (primary and secondary)
 * 
 * TIPS:
 * - Keep slide titles concise and impactful
 * - Use emojis to add visual interest and personality
 * - Ensure text is scannable (short paragraphs, bullet points)
 * - Test all colors for sufficient contrast
 * - Preview on multiple screen sizes
 */

export const euroleaderPartnership: PartnershipConfig = {
  // BASIC INFO
  id: 'euroleader', // lowercase, hyphenated (e.g., 'gates-foundation')
  
  partner: {
    name: 'Euroleader', // Full legal/brand name
    logo: '/uploads/euroleader.png', // Path to logo in /public/uploads/
    colors: {
      primary: '#16273A',   // Main brand color (hex)
      secondary: '#ff9902', // Accent color (hex)
    },
  },
  
  // SLIDES CONFIGURATION
  slides: {
    // Slide 1: Title Slide
    title: {
      enabled: true,
      title: 'Allegory & Euroleader Partnership',
      subtitle: 'Accelerating grant success through AI-powered infrastructure and expert guidance',
    },
    
    // Slide 2: The Opportunity
    opportunity: {
      enabled: true,
      challenges: {
        title: '❌ Current Challenges',
        items: [
          { icon: '⏱️', text: 'Grant application process takes weeks/months to complete' },
          { icon: '📉', text: 'Average win rates remain at 10% across the industry' },
          { icon: '📄', text: 'Settlement process adds months of time and overhead' },
          { icon: '🚧', text: 'No unified platform conecting grant seekers with funders' },
        ],
      },
      model: {
        title: '✨ The New Model',
        items: [
          { icon: '🎯', text: 'AI-Powered platform reducing application time from weeks/months to hours' },
          { icon: '🔍', text: 'Expert guidance achieving higher win rates, 50% in EU, 25% US' },
          { icon: '🤝', text: 'Unified payment infrastructure reducing settlement to < 30 days' },
          { icon: '⚡', text: 'Scalable technology with deep industry expertise' },
        ],
      },
    },
    
    // Slide 3: Strategic Alignment
    alignment: {
      enabled: true,
      allegoryMission: "Removing the friction in grant funding",
      partnerVision: "Maximizing success with grant consulting expertise across Europe and the US",
      sharedPrinciples: [
        { icon: '🎯', title: 'Efficiency through Technology and Expertise' },
        { icon: '💡', title: 'International Market Access' },
        { icon: '🚀', title: 'Post-Award Settlement Innovation' },
      ],
    },
    
    // Slide 4: Allegory's Funding OS
    fundingOS: {
      enabled: true,
      features: [
        { 
          icon: '🔎',
          title: 'Supercharged Search Intelligence',
          desc: 'Deep research and analysis of grant opportunities, reducing time to identify relevant grants' 
        },
        { 
          title: 'Scout AI',
          isScout: true,
          desc: 'Intelligent proposal creation from existing documents, reducing application burden from weeks to hours' 
        },
        
        { 
          icon: '📋',
          title: 'Chrome Extension',
          desc: 'Form extraction behind authentication for seamless application processes' 
        },
        { 
          icon: '💰',
          title: 'Settlement Infrastructure',
          desc: 'Long-term vision to reduce settlement in minutes not months - a gap no current solutions address' 
        },
      ],
    },
    
    // Slide 5: Partner's Model/Approach
    partnerModel: {
      enabled: false,
      title: "Partner's Framework Name",
      subtitle: 'Framework description',
      capabilities: [
        'Capability 1',
        'Capability 2',
        'Capability 3',
        'Capability 4',
        'Capability 5',
      ],
      dataStreams: [
        { icon: '💵', label: 'Data Stream 1' },
        { icon: '⚡', label: 'Data Stream 2' },
        { icon: '🎯', label: 'Data Stream 3' },
      ],
    },
    
    // Slide 6: Phase 1 Details
    phase1: {
      enabled: true,
      title: 'Phase 1: Immediate Partnership',
      dateRange: 'Dec 2025 - Jan 2026',
      deliverables: [
        {
          title: 'Finalize Partnership Agreement',
          cost: '', // Optional
          features: [
            'Finalize commission structure agreement',
            'Onboard Euroleader team to platform',
            'Announce partnership to the market',
          ],
        },
        {
          title: 'Data and Case Study Access',
          features: [
            'Access to successful completed grant applications',
            'Pipeline information on grants with market demand',
            'Settlement process data for automation development',
          ],
        },
        {
          title: 'Design Partnership & Africa Market Proposal',
          features: [
            'Design Partnership for settlement automation process',
            'Design proposal for Africa market entry',
          ],
        },
      ],
      metrics: {
        timeline: '8 Weeks',
        successMetric: '30 customer referrals',
      },
    },
    
    // Slide 7: Timeline (optional)
    timeline: {
      enabled: true,
      title: 'Phase 1: Timeline & Milestones',
      subtitle: 'Building the foundation for long-term collaboration',
      items: [
        {
          week: 'Week 1',
          action: 'Kickoff Meeting',
          detail: 'Finalize commission structure agreement and onboard Euroleader team to platform',
        },
        {
          week: 'Week 2',
          action: 'Onboarding',
          detail: 'Train Euroleader team on platform and grant application process',
        },
        {
          week: 'Week 3',
          action: 'Africa Market Entry',
          detail: 'Deliver proposal for Africa market entry',
        },
      ],
      milestoneTitle: 'Phase 1 Complete!',
      milestoneDescription: 'Initiate Phase 2 – Advisory Partnership',
    },
    
    // Slide 8: Phase 2 Long-term (optional)
    phase2: {
      enabled: true,
      title: 'Phase 2: Advisory Partnership',
      subtitle: 'Scaling through networks and credibility',
      years: [
        {
          label: 'Months 1-2: Foundation & Launch',
          title: 'Foundation & Launch',
          activities: [
            'Advisory role to Allegory with equity stake',
            '15 meetings with VCs secured to accelerate Allegory\'s growth',
          ],
        },
        {
          label: 'Months 3-4: Growth',
          title: 'Growth',
          activities: [
            'Secure and implement digital grant solutions in Africa',
            'Talk to an expert flow in Allegory for expert consultation',
          ],
        },
        {
          label: 'Months 5-6: Expansion & Scaling',
          title: 'Expansion & Scaling',
          activities: [
            'Deploy and test the settlement workflow',
          ],
        },
      ],
      showTotalValue: false, // Set to true to show total value
      totalValue: '$X.XM', // Optional: total partnership value
    },
    
    // Slide 9: Benefits for Partner (optional)
    benefitsPartner: {
      enabled: true,
      title: 'Strategic Benefits for Euroleader',
      subtitle: 'Technology meets expertise',
      usePartnerColors: true, // Use partner brand colors
      benefits: [
        {
          icon: '🛡️',
          title: 'Revenue Diversification',
          desc: '20% commission structure in year 1 for annual subscriptions provides immediate upside on customer referrals while maintaining consulting services',
        },
        {
          icon: '⚡',
          title: 'Scalable Infrastructure',
          desc: 'Access to AI-Powered platform that accelerates consultant workflow and capacity',
        },
        {
          icon: '🌍',
          title: 'Market Expansion',
          desc: 'Platform enables international reach beyond current US/EU operations, particularly Africa opportunity',
        },
      ],
    },
    
    // Slide 10: Benefits for Allegory (optional)
    benefitsAllegory: {
      enabled: true,
      title: 'Strategic Benefits for Allegory',
      subtitle: 'Mutual value creation',
      benefits: [
        {
          icon: '✅',
          title: 'Industry Validation',
          desc: 'Partnership with 20-year established leader validates product with proven experts',
        },
        {
          icon: '🎯',
          title: 'Aligning with Success',
          desc: 'Learning best practices, working in the trenches to accelerate product development',
        },
        {
          icon: '📢',
          title: 'Network Leverage',
          desc: 'Connections and VC introductions open doors across US/EU markets and beyond',
        },
      ],
    },
    
    // Slide 11: Contract Terms (optional)
    contract: {
      enabled: true, // Usually disabled for external presentations
      phase1: {
        type: 'Contract type',
        duration: 'Duration',
        payment: 'Payment terms',
        success: 'Success criteria',
      },
      phase2: {
        type: 'Contract type',
        duration: 'Duration',
        exclusivity: 'Exclusivity terms',
      },
      showPricing: false, // Set to true to show pricing details
    },
    
    // Slide 12: Why This Works (optional)
    whyWorks: {
      enabled: true,
      partnerBrings: [
        'Industry Experts',
        'Exceptional Win Rates',
        'Trusted Relationships',
        'EU & US Grant Expertise',
      ],
      allegoryBrings: [
        'Cutting-Edge AI-Powered Platform',
        'Speed to Market Execution',
        'AI and Automation Capabilities',
        'Scalable Infrastructure',
        'Collaboration at the Heart',
      ],
      togetherStatement: 'Together we align expertise and speed to build a unique value proposition for our clients'

    },
    
    // Slide 13: Call to Action
    cta: {
      enabled: true,
      title: "Let's Change the Grant Game Together",
      nextSteps: [
        {
          icon: '📅',
          step: 'Next Week',
          desc: 'Finalize commission structure and align on measurable outcomes',
        },
        {
          icon: '🔧',
          step: 'December 2025',
          desc: 'Onboard Euroleader team to the Allegory platform, begin first grant applications',
        },
        {
          icon: '🚀',
          step: 'January 2026',
          desc: 'Establish Advisory Partnership and begin scaling through networks and credibility',
        },
      ],
      closingStatement: 'A unique opportunity to combine world-class expertise with transformative technology. Together, we can reshape how grants are discovered, applied for, and managed - creating the future of grant funding.',
    },
  },
};

